import React, { useState, useEffect } from 'react';
import API_BASE from '../config';
import '../translate/LiveTranslate.css';
import {
  TRANSLATE_DOMAINS as DOMAINS,
  TRANSLATE_LANGUAGES as LANGUAGES,
  loadTranslateSettings,
  resolveRemoteLang,
} from '../lib/translateSettings';
import { findContactByNumber } from '../lib/lineContacts';

/**
 * In-call translate control. Starts immediately using prefs from the
 * Settings tab (shared localStorage + per-line caller language).
 */
export default function TranslateControls({
  callSid,
  authToken,
  isTranslating,
  onTranslateChange,
  onClose,
  selectedNumber = '',
  remoteNumber = '',
  savedContacts = [],
  manualLang = '',
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(() => loadTranslateSettings(selectedNumber));
  const [captions, setCaptions] = useState([]);
  const [translateStatus, setTranslateStatus] = useState('active'); // active | reconnecting | failed
  // Phase-2: other-party language locked after first speech (captions WS).
  const [detectedLang, setDetectedLang] = useState('');
  const [detectedLabel, setDetectedLabel] = useState('');

  useEffect(() => {
    setSettings(loadTranslateSettings(selectedNumber));
  }, [selectedNumber]);

  // Remote language actually used for this call: saved contact's
  // translate_lang > a language chosen manually this session > the
  // per-line/global setting (mirrors resolveRemoteLang in lib). We never
  // write back to settings here, so a manual choice is never overwritten.
  const savedContact = findContactByNumber(savedContacts, remoteNumber);
  const contactLang = (savedContact && savedContact.translate_lang) || '';
  const remoteLang = resolveRemoteLang(contactLang, manualLang, settings.remoteLang);
  // Auto-detect only when the contact has no saved language — otherwise we
  // already know what they speak. Server also requires TRANSLATE_AUTODETECT=1.
  const wantAutodetect = !contactLang;

  const handleEnable = React.useCallback(async (isAutoRetry = false) => {
    if (!callSid) {
      setError('No active call to translate.');
      return;
    }
    setLoading(true);
    if (!isAutoRetry) setError('');
    try {
      const res = await fetch(`${API_BASE}/api/translate/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          call_sid: callSid,
          user_lang: settings.userLang,
          remote_lang: remoteLang,
          domain: settings.domain,
          auto_detect: settings.autoDetect,
          autodetect_lang: wantAutodetect,
        }),
      });
      const data = await res.json();
      if (data.status === 'enabled' || data.status === 'already_active') {
        // usedLang for post-call recall: detected wins once it arrives via WS;
        // until then seed with the enable remote_lang.
        onTranslateChange(true, remoteLang);
        setError('');
        setLoading(false);
        return;
      }
      // 409 here means the callee's leg hadn't yet reported 'in-progress'
      // to the carrier's REST API at the moment we asked — a real but
      // short-lived race, since this panel fires its first attempt the
      // instant it mounts, which can be a beat ahead of that REST status
      // catching up even though the call is already audibly connected.
      // Retry silently a few times before surfacing it as an error the
      // user has to notice and manually retry — otherwise a one-off race
      // permanently strands the call with translation silently never
      // having started (the rest of the call still "works", just
      // untranslated, which looks exactly like the other side going
      // silent).
      if (res.status === 409 && autoRetryCount.current < 3) {
        autoRetryCount.current += 1;
        setTimeout(() => handleEnable(true), 1500);
        return;
      }
      setError(data.error || data.detail?.msg || 'Could not start translation.');
    } catch (e) {
      console.error('[Translate] Enable failed:', e);
      setError('Could not reach translation service.');
    }
    setLoading(false);
  }, [callSid, authToken, settings, onTranslateChange, remoteLang, wantAutodetect]);

  const didStart = React.useRef(false);
  const autoRetryCount = React.useRef(0);
  useEffect(() => {
    if (!isTranslating && !didStart.current) {
      didStart.current = true;
      handleEnable();
    }
  }, [isTranslating, handleEnable]);

  // Live captions + translation-status WebSocket. Opens once translation is
  // active and mirrors LiveTranslate's /api/translate/live lifecycle (auth
  // token in the first JSON frame, cleanup on unmount/disable). The server
  // pushes {"type":"text","speaker":"you"|"them","text":...} caption lines,
  // {"type":"status","state":"active"|"reconnecting"|"failed"}, and
  // {"type":"language","lang","label","action"} when Phase-2 autodetect locks.
  const captionsWsRef = React.useRef(null);
  const wsUrl = React.useCallback((path) => {
    if (API_BASE) return API_BASE.replace(/^http/, 'ws') + path;
    if (window.location.hostname === 'localhost' && window.location.port === '3000') {
      return `ws://127.0.0.1:5001${path}`; // CRA dev proxy vs. HMR websocket
    }
    return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}${path}`;
  }, []);

  useEffect(() => {
    if (!isTranslating) return;
    setCaptions([]);
    setTranslateStatus('active');
    setDetectedLang('');
    setDetectedLabel('');
    let ws;
    let active = true;
    try {
      ws = new WebSocket(wsUrl(`/api/translate/captions?call_sid=${encodeURIComponent(callSid)}`));
      captionsWsRef.current = ws;
    } catch (e) {
      console.error('[Translate] captions WS open failed:', e);
      return;
    }
    ws.onopen = () => {
      ws.send(JSON.stringify({ token: authToken, call_sid: callSid }));
    };
    ws.onmessage = (ev) => {
      if (!active) return;
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === 'status') {
          setTranslateStatus(msg.state === 'failed' ? 'failed' : msg.state);
        } else if (msg.type === 'language' && msg.lang) {
          const code = String(msg.lang).split(/[-_]/)[0];
          const label = msg.label || LANGUAGES[code] || code;
          setDetectedLang(code);
          setDetectedLabel(label);
          // Feed post-call recall with the locked language (lock/idle only;
          // fallback keeps the enable-time remote_lang already seeded).
          if (msg.action === 'lock' || msg.action === 'idle') {
            onTranslateChange(true, code);
          }
        } else if (msg.type === 'text' && msg.text) {
          setCaptions((prev) => [
            ...prev.slice(-39),
            {
              speaker: msg.speaker === 'you' ? 'you' : 'them',
              text: msg.text,
              id: Date.now() + Math.random(),
            },
          ]);
        }
      } catch (e) {
        // ignore non-JSON / malformed frames
      }
    };
    ws.onclose = () => {
      if (active) captionsWsRef.current = null;
    };
    return () => {
      active = false;
      try { ws.close(); } catch (e) {}
      captionsWsRef.current = null;
    };
  }, [isTranslating, callSid, authToken, wsUrl, onTranslateChange]);

  const handleDisable = async () => {
    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/translate/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ call_sid: callSid }),
      });
      onTranslateChange(false);
    } catch (e) {
      console.error('[Translate] Disable failed:', e);
    }
    setLoading(false);
  };

  const handleRestart = async () => {
    // A Gemini leg that failed even its one reconnect attempt is dead and
    // /enable alone would just return 'already_active' for the stale session.
    // Tear it down via /disable, then re-enable to build a fresh session.
    // We call the endpoints directly (not handleDisable) so the sheet stays
    // open and isTranslating is not toggled in between.
    setLoading(true);
    setTranslateStatus('active');
    setCaptions([]);
    try {
      try {
        await fetch(`${API_BASE}/api/translate/disable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ call_sid: callSid }),
        });
      } catch (e) {
        console.error('[Translate] Restart disable failed:', e);
      }
      autoRetryCount.current = 0;
      await new Promise((r) => setTimeout(r, 400));
      await handleEnable();
    } finally {
      setLoading(false);
    }
  };

  if (isTranslating) {
    const indicator =
      translateStatus === 'failed' ? '⚠️ Translation ended' :
      translateStatus === 'reconnecting' ? '🔄 Reconnecting…' :
      '🌐 Translating';
    const indicatorClass =
      translateStatus === 'failed' ? ' translate-indicator-failed' :
      translateStatus === 'reconnecting' ? ' translate-indicator-reconnecting' :
      '';
    return (
      <div className="translate-controls translate-active">
        <div className="translate-header">
          <span className={`translate-indicator${indicatorClass}`}>{indicator}</span>
          <span className="translate-pair">
            {LANGUAGES[settings.userLang]} ↔ {LANGUAGES[detectedLang] || LANGUAGES[remoteLang]}
          </span>
          {detectedLabel ? (
            <span className="translate-detected">Detected: {detectedLabel}</span>
          ) : null}
          <span className="translate-domain">{DOMAINS[settings.domain]}</span>
        </div>
        {translateStatus === 'reconnecting' && (
          <div className="translate-status">Trying to restore the translation connection…</div>
        )}
        {error && <div className="translate-warning">{error}</div>}
        {captions.length > 0 && (
          <div className="translate-captions">
            {captions.map((c) => (
              <div key={c.id} className={`translate-caption-line translate-caption-${c.speaker}`}>
                <span className="translate-caption-who">{c.speaker === 'you' ? 'You' : 'Them'}</span>
                <span className="translate-caption-text">{c.text}</span>
              </div>
            ))}
          </div>
        )}
        {translateStatus === 'failed' ? (
          <>
            <div className="translate-warning">Translation was interrupted and could not reconnect. Only one automatic retry is attempted.</div>
            <button
              className="btn-primary translate-start"
              onClick={handleRestart}
              disabled={loading}
            >
              {loading ? 'Restarting…' : 'Restart Translation'}
            </button>
          </>
        ) : (
          <button
            className="btn-secondary translate-stop"
            onClick={handleDisable}
            disabled={loading}
          >
            {loading ? 'Stopping…' : 'Stop Translation'}
          </button>
        )}
        <button className="translate-close" onClick={onClose}>✕</button>
      </div>
    );
  }

  return (
    <div className="translate-controls translate-setup">
      <div className="translate-header">
        <span>🌐 Live Translate</span>
        <button className="translate-close" onClick={onClose}>✕</button>
      </div>
      <div className="translate-pair">{LANGUAGES[settings.userLang]} ↔ {LANGUAGES[remoteLang]}</div>
      <p className="translate-settings-hint">Languages are set in Settings.</p>
      {loading && <div className="translate-status">Starting translation…</div>}
      {error && (
        <>
          <div className="translate-warning">{error}</div>
          <button
            className="btn-primary translate-start"
            onClick={() => {
              autoRetryCount.current = 0;
              handleEnable();
            }}
            disabled={loading}
          >
            Retry
          </button>
        </>
      )}
    </div>
  );
}
